import moment from 'moment-timezone'
import axios from 'axios'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'

import getColor from '~/lib/getColor'
import logger from '~/lib/logger'
import { lastCheckUpdatesTimeStorage } from '~/lib/services/WebStorage/LastCheckUpdatesTimeStorage'

/**
 * The whole logic of check updates
 * Start timer for "intervalBetweenChecks" after which fire api call to get current app version
 * When call api set current timestamp to session storage
 * When tab becomes active after hibernate/sleep mode or just switched from another tab
 * if after tab became active, and it was more than "minutesBetweenCheckUpdates" check for updates
 *
 * In case if versions from check and current build aren't equal show pop to user with suggestion to reload page due to new updates
 * If current app version is "localhost", meaning we are running ui app locally - skip checking versions
 * (it's kind of obvious that you need refresh page after local changes, and frankly it's done by hot refresh)
 */

const intervalBetweenChecks = moment.duration(30, 'minutes')
const minutesBetweenCheckUpdates = 10
let timerId: number
const releaseId = __RELEASE_ID__
let isUpdatesAvailableModalOpened = false

function popModal() {
    isUpdatesAvailableModalOpened = true

    Modal.confirm({
        title: 'Application updates available',
        content:
            'Our app just got better! Click reload to get the latest updates.',
        okText: 'Reload app',
        cancelText: 'Close',
        icon: <InfoCircleOutlined style={{ color: getColor('shroudBlue') }} />,
        onOk() {
            isUpdatesAvailableModalOpened = false
            window.location.reload()
        },
        onCancel() {
            isUpdatesAvailableModalOpened = false
        },
    })
}

function resetTimer() {
    clearTimeout(timerId)
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    timerId = window.setTimeout(checkForNewCode, Number(intervalBetweenChecks))
}

export default function initiateAppRefresh() {
    resetTimer()

    /**
     * Check updates when tab changes its status, and it becomes active(switching tab exiting hibernating/sleep mode)
     */
    document.addEventListener('visibilitychange', () => {
        const last = lastCheckUpdatesTimeStorage.getItem()
        const now = moment(Date.now())
        /**
         * If there weren't previous check on this tab make check now
         */
        const diff = last
            ? now.diff(moment(last), 'minutes')
            : minutesBetweenCheckUpdates

        if (!document.hidden && diff >= minutesBetweenCheckUpdates) {
            resetTimer()
            checkForNewCode()
        }
    })
}

async function checkForNewCode() {
    const isLocalDevelopment = releaseId === 'localhost'

    /**
     * skip if modal already opened or we on locally running app
     */
    if (isUpdatesAvailableModalOpened || isLocalDevelopment) {
        return
    }

    lastCheckUpdatesTimeStorage.setItem()

    try {
        const { data } = await axios.get('/api/v1/identity/configuration')

        if (releaseId !== data['FICTIV_JS_RELEASE_ID']) {
            popModal()
        }
        resetTimer()
    } catch (error) {
        logger.exception(error, {
            extra: { info: 'Failed to get configuration' },
        })
    }
}
